import {
  GetIssueResponseDtoActionProgressEnum,
  GetIssueResponseDtoApprovalStatusEnum,
  GetIssueResponseDtoStatusEnum,
} from '@issuemanager/api-client';
import {
  IssueActionProgressEnum,
  IssueApprovalStatusEnum,
  IssueStatusEnum,
} from '@issuemanager/constants';

type Params = {
  status: GetIssueResponseDtoStatusEnum | undefined;
  actionProgress?: GetIssueResponseDtoActionProgressEnum | undefined;
  approvalStatus?: GetIssueResponseDtoApprovalStatusEnum | undefined;
};

export const useIssueStatusInfo = ({
  status,
  actionProgress,
  approvalStatus,
}: Params): {
  isEditable: boolean;
  isInitiated: boolean;
  isNotYetInitiated: boolean;
  isClosedOrDiscarded: boolean;
  isNotYetAssigned: boolean;
  isReadyToApprove: boolean;
} => {
  const issueStatusDefaultValues = {
    isEditable: false,
    isInitiated: false,
    isNotYetInitiated: false,
    isClosedOrDiscarded: false,
    isNotYetAssigned: false,
    isReadyToApprove: false,
  };

  if (!status) {
    return issueStatusDefaultValues;
  }

  const closedOrDiscardedOptions: GetIssueResponseDtoStatusEnum[] = [
    IssueStatusEnum.CLOSED,
    IssueStatusEnum.DISCARDED,
  ];

  const isClosedOrDiscarded = closedOrDiscardedOptions.includes(status);

  if (!actionProgress || !approvalStatus) {
    return {
      ...issueStatusDefaultValues,
      isClosedOrDiscarded,
    };
  }

  const editableOptions: GetIssueResponseDtoStatusEnum[] = [
    GetIssueResponseDtoStatusEnum.Open,
    GetIssueResponseDtoStatusEnum.InProgress,
  ];

  const isEditable = editableOptions.includes(status);

  const initiatedOptions: GetIssueResponseDtoActionProgressEnum[] = [
    IssueActionProgressEnum.INITIATED,
    IssueActionProgressEnum.ONGOING,
  ];

  const isInitiated = Boolean(
    status === IssueStatusEnum.IN_PROGRESS &&
      initiatedOptions.includes(actionProgress),
  );

  const notInitiatedOptions: GetIssueResponseDtoActionProgressEnum[] = [
    IssueActionProgressEnum.NOT_INITIATED,
    IssueActionProgressEnum.BLANK,
  ];

  const isNotYetInitiated =
    status === IssueStatusEnum.IN_PROGRESS &&
    notInitiatedOptions.includes(actionProgress);

  const isNotYetAssigned = status === IssueStatusEnum.OPEN;

  const isReadyToApprove = Boolean(
    status === IssueStatusEnum.TO_BE_APPROVED &&
      approvalStatus === IssueApprovalStatusEnum.WAITING_FOR_APPROVAL,
  );

  return {
    isEditable,
    isInitiated,
    isNotYetInitiated,
    isClosedOrDiscarded,
    isNotYetAssigned,
    isReadyToApprove,
  };
};
